import React,{useState,useEffect,useCallback,useRef,useMemo} from 'react';
import {invoiceAPI,paymentAPI,settingsAPI,customerAPI,productAPI,branchAPI,inventoryAPI,orderAPI,currencyAPI} from '../utils/api';
import {SearchBar,FilterButtons,fmt,fmtDate} from '../components/common';
import {useLang} from '../i18n';
import {useAuth} from '../context/AuthContext';
import {Plus,X,Package,Search,ChevronDown,Trash2,Save,FileText,
  RefreshCw,Edit2,Layers,ArrowLeft,ChevronRight,DollarSign,
  Printer,AlertTriangle,ToggleLeft,ToggleRight,ShieldOff,Lock} from 'lucide-react';
import toast from 'react-hot-toast';

/* ── useWindowWidth ─────────────────────────────────────────── */
function useWindowWidth(){
  const[w,setW]=useState(()=>typeof window!=='undefined'?window.innerWidth:1200);
  useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener('resize',h);return()=>window.removeEventListener('resize',h)},[]);
  return w;
}

/* ── Print style ─────────────────────────────────────────────── */
// Open print popup for invoice/order
function openPrintWindow(elementId){
  const el=document.getElementById(elementId);
  if(!el){alert('Nothing to print');return;}
  const html=`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Invoice</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Noto+Serif+Khmer:wght@400;700;900&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;}
  body{font-family:'Noto Serif Khmer','Khmer OS',Arial,sans-serif;font-size:12px;color:#000;background:#fff;margin:0;padding:0;}
  @page{margin:12mm;size:A4;}
  table{border-collapse:collapse;}
  img{max-width:100%;}
</style>
</head>
<body>${el.innerHTML}</body>
</html>`;
  const pw=window.open('','_blank','width=900,height=700,scrollbars=yes');
  if(!pw){alert('Please allow popups to print');return;}
  pw.document.write(html);
  pw.document.close();
  pw.focus();
  setTimeout(()=>{pw.print();pw.close();},800);
}

/* ── Constants ───────────────────────────────────────────────── */
const PERMS={VIEW:'invoices.view',CREATE:'invoices.create',EDIT:'invoices.edit',DELETE:'invoices.delete',PAYMENT:'invoices.payment'};
const ALL_INV_STATUSES=['draft','sent','paid','partial','overdue','cancelled'];
const FILTER_TABS=['All',...ALL_INV_STATUSES];
const S_CFG={
  draft:   {color:'#64748b',bg:'#f1f5f9',label:'Draft'},
  sent:    {color:'#2563eb',bg:'#dbeafe',label:'Sent'},
  paid:    {color:'#16a34a',bg:'#dcfce7',label:'Paid'},
  partial: {color:'#d97706',bg:'#fef3c7',label:'Partial'},
  overdue: {color:'#dc2626',bg:'#fee2e2',label:'Overdue'},
  cancelled:{color:'#9d174d',bg:'#fce7f3',label:'Cancelled'},
};

const SPill=({s,size=11})=>{
  const c=S_CFG[s]||{color:'#64748b',bg:'#f1f5f9',label:s};
  return<span style={{display:'inline-flex',alignItems:'center',background:c.bg,color:c.color,padding:'3px 10px',borderRadius:20,fontSize:size,fontWeight:700,textTransform:'capitalize',whiteSpace:'nowrap'}}>{c.label||s}</span>;
};
const NoPerm=({action})=>(
  <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--dm)',borderRadius:8,fontSize:12,color:'var(--danger)',marginBottom:12}}>
    <ShieldOff size={14}/> {t('common.accessDenied')}
  </div>
);
const StockBadge=({qty,reorderLevel=10,allowNeg=false})=>{
  if(qty==null)return<span style={{fontSize:10,color:'var(--td)'}}>No data</span>;
  const out=qty<=0;const low=!out&&qty<=reorderLevel;
  return<span style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:5,background:out?'var(--dm)':low?'var(--wm)':'var(--sm)',color:out?'var(--danger)':low?'var(--warning)':'var(--success)'}}><Package size={9}/>{out?(allowNeg?'0 (neg.ok)':'Out'):low?`Low: ${qty}`:`Stock: ${qty}`}</span>;
};

/* ── Product Picker (with stock) ─────────────────────────────── */
function ProductPicker({value,products,invMap,branchId,onSelect}){
  const[open,setOpen]=useState(false);const[q,setQ]=useState('');
  const wrapRef=useRef();const inputRef=useRef();
  const selected=useMemo(()=>products.find(p=>p.id===value),[products,value]);
  useEffect(()=>{const h=e=>{if(wrapRef.current&&!wrapRef.current.contains(e.target))setOpen(false)};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h)},[]);
  useEffect(()=>{if(open&&inputRef.current)inputRef.current.focus()},[open]);
  const getInv=pid=>{if(!branchId||!pid)return null;return invMap[`${pid}_${branchId}`]??null};
  const filtered=useMemo(()=>{const ql=q.trim().toLowerCase();if(!ql)return products.slice(0,40);return products.filter(p=>(p.name||'').toLowerCase().includes(ql)||(p.sku||'').toLowerCase().includes(ql)).slice(0,30)},[q,products]);
  return(
    <div ref={wrapRef} style={{position:'relative'}}>
      <div onClick={()=>setOpen(v=>!v)} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:8,border:`1.5px solid ${open?'var(--primary)':'var(--border)'}`,background:'var(--s2)',cursor:'pointer',minHeight:40,userSelect:'none',transition:'border-color .15s'}}>
        {selected?(
          <div style={{flex:1,display:'flex',alignItems:'center',gap:8,minWidth:0}}>
            {selected.image_url?<img src={selected.image_url} alt="" style={{width:24,height:24,borderRadius:5,objectFit:'cover',flexShrink:0}}/>:<div style={{width:24,height:24,borderRadius:5,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={11} color="var(--primary)"/></div>}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{selected.name}</div>
              <div style={{fontSize:11,color:'var(--td)'}}>{selected.sku} · {selected.uom?.abbreviation||'—'}</div>
            </div>
            {branchId&&(()=>{const inv=getInv(selected.id);return<StockBadge qty={inv?.quantity??null} reorderLevel={selected.reorder_level} allowNeg={selected.allow_negative_stock}/>})()}
          </div>
        ):<span style={{fontSize:13,color:'var(--td)',flex:1}}>— Select product —</span>}
        <ChevronDown size={13} color="var(--td)" style={{flexShrink:0,transition:'transform .15s',transform:open?'rotate(180deg)':'none'}}/>
      </div>
      {open&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:9000,background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:10,boxShadow:'0 8px 40px rgba(0,0,0,.2)',display:'flex',flexDirection:'column',maxHeight:280}}>
          <div style={{padding:'8px 10px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
            <Search size={13} color="var(--td)"/>
            <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)} placeholder="Search name or SKU..." style={{border:'none',background:'transparent',color:'var(--text)',fontSize:13,outline:'none',flex:1}} onKeyDown={e=>e.key==='Escape'&&setOpen(false)}/>
            {q&&<button style={{border:'none',background:'none',cursor:'pointer',color:'var(--td)',padding:0}} onClick={()=>setQ('')}><X size={11}/></button>}
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.length===0?<div style={{padding:'20px',textAlign:'center',color:'var(--td)',fontSize:13}}>
              <Package size={24} style={{opacity:.2,display:'block',margin:'0 auto 8px'}}/>
              {products.length===0?'No products stocked at this branch':'No products found'}
              {products.length===0&&<div style={{fontSize:11,marginTop:4}}>Add stock at this branch first</div>}
            </div>
              :filtered.map(p=>{const inv=getInv(p.id);const qty=inv?.quantity??null;const out=qty!=null&&qty<=0&&!p.allow_negative_stock;return(
                <div key={p.id} onClick={()=>{onSelect(p,inv);setOpen(false);setQ('')}} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',cursor:'pointer',borderBottom:'1px solid var(--border)',opacity:out?.5:1}} onMouseEnter={e=>e.currentTarget.style.background='var(--s2)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  {p.image_url?<img src={p.image_url} alt="" style={{width:32,height:32,borderRadius:7,objectFit:'cover',flexShrink:0}}/>:<div style={{width:32,height:32,borderRadius:7,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={14} color="var(--primary)"/></div>}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600}}>{p.name}</div>
                    <div style={{fontSize:11,color:'var(--td)',display:'flex',gap:8,marginTop:1}}><span>SKU: {p.sku}</span>{p.uom&&<span>{p.uom.abbreviation}</span>}</div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <div style={{fontSize:12,fontWeight:800,color:'var(--primary)',marginBottom:2}}>{fmt(p.sale_price)}</div>
                    <StockBadge qty={qty} reorderLevel={p.reorder_level} allowNeg={p.allow_negative_stock}/>
                  </div>
                </div>
              )})}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Mobile Item Card ────────────────────────────────────────── */
function MobileItemCard({item,idx,products,invMap,branchId,onChange,onRemove}){
  const prod=useMemo(()=>products.find(p=>p.id===item.product_id),[products,item.product_id]);
  const lineTotal=+((item.quantity||0)*(item.unit_price||0)-(item.discount_amount||0)).toFixed(2);
  const uomOpts=useMemo(()=>{if(!prod)return[];const opts=[];if(prod.uom)opts.push({id:prod.uom.id,label:prod.uom.abbreviation,price:parseFloat(prod.sale_price)||0,factor:1});(prod.uoms||[]).forEach(pu=>{if(pu.uom&&(!prod.uom||pu.uom.id!==prod.uom.id))opts.push({id:pu.uom.id,label:pu.uom.abbreviation,price:parseFloat(pu.price)||parseFloat(prod.sale_price)||0,factor:parseFloat(pu.conversion_factor)||1})});return opts},[prod]);
  const inv=invMap[`${item.product_id}_${branchId}`]??null;
  const overStock=prod&&!prod.allow_negative_stock&&inv?.quantity!=null&&inv.quantity>0&&item.quantity>inv.quantity;
  const noStock=prod&&!prod.allow_negative_stock&&inv?.quantity!=null&&inv.quantity<=0;
  const handleProductSelect=(p,stockRec)=>onChange(idx,{product_id:p.id,unit_price:parseFloat(p.sale_price)||0,cost_price:parseFloat(p.cost_price)||0,uom_id:p.uom_id||null,quantity:item.quantity||1,discount_amount:0,uom_conversion_factor:1});
  const handleUomChange=uomId=>{const opt=uomOpts.find(o=>o.id===parseInt(uomId));if(opt)onChange(idx,{uom_id:opt.id,unit_price:opt.price})};
  const inputStyle={fontSize:13,padding:'8px 10px',height:40,width:'100%',background:'var(--surface)',border:`1.5px solid ${(overStock||noStock)?'var(--danger)':'var(--border)'}`,borderRadius:8,color:'var(--text)',textAlign:'right',boxSizing:'border-box'};
  return(
    <div style={{background:'var(--s2)',borderRadius:12,padding:14,marginBottom:10,border:`1px solid ${(overStock||noStock)?'var(--danger)':'var(--border)'}`}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <span style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5}}>Line {idx+1}</span>
        <button onClick={()=>onRemove(idx)} style={{background:'var(--dm)',border:'none',borderRadius:6,cursor:'pointer',color:'var(--danger)',padding:'4px 8px',display:'flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600}}><X size={11}/> Remove</button>
      </div>
      <div style={{marginBottom:10}}>
        <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Product</label>
        <ProductPicker value={item.product_id} products={products} invMap={invMap} branchId={branchId} onSelect={handleProductSelect}/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>UOM</label>
          <select value={String(item.uom_id||prod?.uom_id||'')} onChange={e=>handleUomChange(e.target.value)} disabled={uomOpts.length<=1} style={{fontSize:13,padding:'8px 10px',height:40,width:'100%',background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)'}}>
            {uomOpts.length>0?uomOpts.map(o=><option key={o.id} value={o.id}>{o.label}</option>):<option value="">{prod?.uom?.abbreviation||'—'}</option>}
          </select>
        </div>
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Qty</label>
          <input type="number" min={0} value={item.quantity??1} onChange={e=>onChange(idx,{quantity:parseFloat(e.target.value)||0})} style={inputStyle}/>
        </div>
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Unit Price</label>
          <input type="number" min={0} step={0.01} value={item.unit_price??0} onChange={e=>onChange(idx,{unit_price:parseFloat(e.target.value)||0})} style={inputStyle}/>
        </div>
        <div>
          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Discount</label>
          <input type="number" min={0} step={0.01} value={item.discount_amount??0} onChange={e=>onChange(idx,{discount_amount:parseFloat(e.target.value)||0})} style={inputStyle}/>
        </div>
      </div>
      {(overStock||noStock)&&(
        <div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'var(--danger)',padding:'6px 10px',background:'var(--dm)',borderRadius:7,marginBottom:8}}>
          <AlertTriangle size={11}/>{noStock?`Out of stock${prod.allow_negative_stock?' (neg. allowed)':''}` :`Needs ${item.quantity}, only ${inv?.quantity} available`}
        </div>
      )}
      <div style={{display:'flex',justifyContent:'flex-end',paddingTop:10,borderTop:'1px solid var(--border)'}}>
        <span style={{fontSize:11,color:'var(--td)',marginRight:8}}>Amount</span>
        <span style={{fontSize:15,fontWeight:800,color:'var(--primary)'}}>{fmt(lineTotal)}</span>
      </div>
    </div>
  );
}

/* ── Desktop Line Item Row ───────────────────────────────────── */
function DesktopItemRow({item,idx,isFirst,products,invMap,branchId,onChange,onRemove,readOnly}){
   const{t}=useLang();
  const prod=useMemo(()=>products.find(p=>p.id===item.product_id),[products,item.product_id]);
  const inv=invMap[`${item.product_id}_${branchId}`]??null;
  const overStock=prod&&!prod.allow_negative_stock&&inv?.quantity!=null&&inv.quantity>0&&item.quantity>inv.quantity;
  const noStock=prod&&!prod.allow_negative_stock&&inv?.quantity!=null&&inv.quantity<=0;
  const lineTotal=+((item.quantity||0)*(item.unit_price||0)-(item.discount_amount||0)).toFixed(2);
  const uomOpts=useMemo(()=>{if(!prod)return[];const opts=[];if(prod.uom)opts.push({id:prod.uom.id,label:prod.uom.abbreviation,price:parseFloat(prod.sale_price)||0,factor:1});(prod.uoms||[]).forEach(pu=>{if(pu.uom&&(!prod.uom||pu.uom.id!==prod.uom.id))opts.push({id:pu.uom.id,label:pu.uom.abbreviation,price:parseFloat(pu.price)||parseFloat(prod.sale_price)||0,factor:parseFloat(pu.conversion_factor)||1})});return opts},[prod]);
  const handleProductSelect=(p)=>onChange(idx,{product_id:p.id,unit_price:parseFloat(p.sale_price)||0,cost_price:parseFloat(p.cost_price)||0,uom_id:p.uom_id||null,quantity:item.quantity||1,discount_amount:0,uom_conversion_factor:1});
  const handleUomChange=uomId=>{const opt=uomOpts.find(o=>o.id===parseInt(uomId));if(opt)onChange(idx,{uom_id:opt.id,unit_price:opt.price,uom_conversion_factor:opt.factor||1})};
  const H=({children})=>isFirst?<div style={{fontSize:9,color:'var(--td)',fontWeight:700,letterSpacing:.5,marginBottom:3,textTransform:'uppercase'}}>{children}</div>:null;
  const inp={fontSize:12,padding:'6px 8px',height:40,background:'var(--s2)',border:`1.5px solid ${(overStock||noStock)?'var(--danger)':'var(--border)'}`,borderRadius:8,color:'var(--text)',textAlign:'right',width:'100%',boxSizing:'border-box'};

  if(readOnly){
    return(
      <tr style={{borderBottom:'1px solid var(--border)'}}>
        <td style={{padding:'10px 12px',fontSize:12,color:'var(--td)',textAlign:'center'}}>{idx+1}</td>
        <td style={{padding:'10px 12px'}}><div style={{fontSize:13,fontWeight:600}}>{prod?.name||'—'}</div><div style={{fontSize:11,color:'var(--td)'}}>{prod?.sku}</div></td>
        <td style={{padding:'10px 12px',fontSize:12,color:'var(--td)',textAlign:'center'}}>{item.uom_id?uomOpts.find(o=>o.id===item.uom_id)?.label:prod?.uom?.abbreviation||'—'}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:600}}>{item.quantity}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13}}>{fmt(item.unit_price)}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:12,color:'var(--danger)'}}>{parseFloat(item.discount_amount||0)>0?`-${fmt(item.discount_amount)}`:'—'}</td>
        <td style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:800,color:'var(--primary)'}}>{fmt(lineTotal)}</td>
      </tr>
    );
  }
  return(
    <div style={{marginBottom:8}}>
      <div style={{display:'grid',gridTemplateColumns:'minmax(160px,2.2fr) 80px 80px 110px 100px 100px 30px',gap:5,alignItems:'start'}}>
        <div><H>Product / SKU</H><ProductPicker value={item.product_id} products={products} invMap={invMap} branchId={branchId} onSelect={handleProductSelect}/></div>
        <div><H>UOM</H>
          <select value={String(item.uom_id||prod?.uom_id||'')} onChange={e=>handleUomChange(e.target.value)} disabled={uomOpts.length<=1} style={{fontSize:12,padding:'6px 8px',height:40,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:8,color:'var(--text)'}}>
            {uomOpts.length>0?uomOpts.map(o=><option key={o.id} value={o.id}>{o.label}</option>):<option value="">{prod?.uom?.abbreviation||'—'}</option>}
          </select>
        </div>
        <div><H>QTY</H><input type="number" min={0} step={1} value={item.quantity??1} onChange={e=>onChange(idx,{quantity:parseFloat(e.target.value)||0})} style={inp}/></div>
        <div><H>UNIT PRICE</H><input type="number" min={0} step={0.01} value={item.unit_price??0} onChange={e=>onChange(idx,{unit_price:parseFloat(e.target.value)||0})} style={inp}/></div>
        <div><H>DISCOUNT $</H><input type="number" min={0} step={0.01} value={item.discount_amount??0} onChange={e=>onChange(idx,{discount_amount:parseFloat(e.target.value)||0})} style={inp}/></div>
        <div><H>LINE TOTAL</H><div style={{height:40,display:'flex',alignItems:'center',justifyContent:'flex-end',fontSize:13,fontWeight:800,paddingRight:6,background:'var(--pm)',borderRadius:8,color:'var(--primary)'}}>{fmt(lineTotal)}</div></div>
        <div>{isFirst&&<div style={{height:17}}/>}<button onClick={()=>onRemove(idx)} style={{color:'var(--danger)',background:'none',border:'none',cursor:'pointer',padding:'6px',height:40,width:30,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:6}}><X size={13}/></button></div>
      </div>
      {prod&&branchId&&(noStock||overStock)&&(
        <div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:'var(--danger)',padding:'3px 8px',background:'var(--dm)',borderRadius:5,marginTop:4,width:'fit-content'}}>
          <AlertTriangle size={10}/>{noStock?`Out of stock${prod.allow_negative_stock?' (neg. allowed)':''}`:`Needs ${item.quantity}, only ${inv?.quantity} available`}
        </div>
      )}
    </div>
  );
}

/* ── Status Stepper ──────────────────────────────────────────── */
function StatusStepper({current,onChange,disabled,mobile}){
  const{t}=useLang();
  return(
    <div style={{padding:'12px 16px',background:'var(--s2)',borderRadius:10,border:'1px solid var(--border)'}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.5,marginBottom:8,textTransform:'uppercase'}}>{t('invoices.changeStatus')}</div>
      <div style={{display:'flex',flexWrap:'wrap',gap:mobile?5:6}}>
        {ALL_INV_STATUSES.map(s=>{
          const cfg=S_CFG[s];const isCurrent=s===current;
          return(
            <button key={s} onClick={()=>!disabled&&s!==current&&onChange(s)} disabled={disabled||isCurrent}
              style={{display:'flex',alignItems:'center',gap:4,padding:mobile?'6px 10px':'5px 11px',borderRadius:20,fontSize:mobile?12:11,fontWeight:700,cursor:(disabled||isCurrent)?'default':'pointer',transition:'all .15s',border:`1.5px solid ${isCurrent?cfg.color:'var(--border)'}`,background:isCurrent?cfg.bg:'var(--surface)',color:isCurrent?cfg.color:'var(--td)',opacity:disabled?.6:1}}>
              {isCurrent&&<span style={{width:6,height:6,borderRadius:'50%',background:cfg.color,flexShrink:0}}/>}
              {cfg.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Invoice Panel ───────────────────────────────────────────── */
const BLANK_ITEM={product_id:null,quantity:1,unit_price:0,discount_amount:0,cost_price:0,uom_id:null,uom_conversion_factor:1};
const BLANK_FORM={customer_id:'',branch_id:'',invoice_date:new Date().toISOString().split('T')[0],due_date:'',status:'draft',tax_rate:0,discount_amount:0,notes:'',payment_terms:'',update_stock:true,items:[{...BLANK_ITEM}]};

function InvoicePanel({invoice,isNew,customers,products,branches,branchInventory,methods,can,onSaved,onDeleted,onPay,onPrint,onClose,isMobile}){
  const{isOwner,myBranchId,myBranch}=useAuth();
const{t}=useLang();
  // Dual-currency helper — self-contained so InvoicePanel doesn't depend on parent scope
  const[panelKhrRate,setPanelKhrRate]=useState(4100);
  useEffect(()=>{currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setPanelKhrRate(parseFloat(rt.rate)||4100);}).catch(()=>{});},[]);
  const fmtKHR=n=>`≈ ៛${Math.round((parseFloat(n)||0)*panelKhrRate).toLocaleString()}`;

  // For new invoices: non-owners auto-get their branch; owners start empty
  const defaultBranchId=(!isOwner&&myBranchId)?String(myBranchId):'';

  const[form,setForm]=useState(()=>{
    if(isNew)return{...JSON.parse(JSON.stringify(BLANK_FORM)),branch_id:defaultBranchId};
    return{customer_id:invoice.customer_id||'',branch_id:invoice.branch_id||'',
      invoice_date:invoice.invoice_date||new Date().toISOString().split('T')[0],
      due_date:invoice.due_date||'',status:invoice.status||'draft',
      tax_rate:parseFloat(invoice.tax_rate)||0,discount_amount:parseFloat(invoice.discount_amount)||0,
      notes:invoice.notes||'',payment_terms:invoice.payment_terms||'',update_stock:false,
      items:invoice.items?.length?invoice.items.map(i=>({product_id:i.product_id,quantity:parseFloat(i.quantity),unit_price:parseFloat(i.unit_price),discount_amount:parseFloat(i.discount_amount||0),cost_price:parseFloat(i.cost_price||0),uom_id:i.uom_id||null,uom_conversion_factor:1})):[{...BLANK_ITEM}],
    };
  });
  const[saving,setSaving]=useState(false);
  const[statusChanging,setStatusChanging]=useState(false);
  const[editMode,setEditMode]=useState(isNew);
  const[delConfirm,setDelConfirm]=useState(false);
  const[invMap,setInvMap]=useState({});const[invLoading,setInvLoading]=useState(false);

  useEffect(()=>{
    if(!form.branch_id){setInvMap({});return;}
    setInvLoading(true);
    inventoryAPI.getAll({branch_id:form.branch_id,limit:1000}).then(r=>{const map={};(r.data.data||[]).forEach(rec=>{map[`${rec.product_id}_${rec.branch_id}`]=rec});setInvMap(map)}).catch(()=>{}).finally(()=>setInvLoading(false));
  },[form.branch_id]);

  const sf=(k,v)=>setForm(f=>({...f,[k]:v}));
  const addItem=()=>setForm(f=>({...f,items:[...f.items,{...BLANK_ITEM}]}));
  const rmItem=i=>setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}));
  const upItem=(i,patch)=>setForm(f=>{const items=[...f.items];items[i]={...items[i],...patch};return{...f,items}});

  const custOpts=[{value:'',label:'— Select Customer —'},...customers.map(c=>({value:String(c.id),label:c.name}))];
  const brOpts=[{value:'',label:'— Select Branch —'},...branches.map(b=>({value:String(b.id),label:b.name}))];

  // Products filtered by branch inventory:
  // - Non-owners: use branchInventory prop (pre-loaded for their fixed branch)
  // - Owners: use invMap (loaded reactively when form.branch_id changes)
  // - No inventory at all (new branch): return null → show ALL products as fallback
  const branchProductIds=useMemo(()=>{
    if(!isOwner&&branchInventory&&branchInventory.length>0){
      return new Set(branchInventory.map(i=>i.product_id));
    }
    if(isOwner&&form.branch_id&&Object.keys(invMap).length>0){
      return new Set(Object.keys(invMap).map(k=>parseInt(k.split('_')[0])).filter(Boolean));
    }
    return null; // null = no filter
  },[isOwner,branchInventory,invMap,form.branch_id]);

  // When branch has no inventory records yet, fall back to all products
  const branchProducts=useMemo(()=>{
    if(!branchProductIds||branchProductIds.size===0)return products;
    return products.filter(p=>branchProductIds.has(p.id));
  },[products,branchProductIds]);
  const statusOpts=ALL_INV_STATUSES.map(s=>({value:s,label:S_CFG[s]?.label||s}));

  const validItems=form.items.filter(i=>i.product_id&&(i.quantity||0)>0);
  const subtotal=+validItems.reduce((s,i)=>s+(i.quantity||0)*(i.unit_price||0)-(i.discount_amount||0),0).toFixed(2);
  const taxAmt=+(subtotal*(parseFloat(form.tax_rate||0)/100)).toFixed(2);
  const total=+(subtotal+taxAmt-parseFloat(form.discount_amount||0)).toFixed(2);

  const warnings=validItems.filter(item=>{const p=branchProducts.find(x=>x.id===item.product_id)||products.find(x=>x.id===item.product_id);const inv=invMap[`${item.product_id}_${form.branch_id}`];if(!p||!inv||p.allow_negative_stock)return false;return item.quantity>inv.quantity});

  const save=async()=>{
    if(!form.customer_id)return toast.error('Please select a customer');
    if(!validItems.length)return toast.error(t('invoices.addAtLeastOne'));
    setSaving(true);
    try{
      const payload={...form,subtotal,tax_amount:taxAmt,total_amount:total,
        customer_id:parseInt(form.customer_id)||form.customer_id,
        branch_id:parseInt(form.branch_id)||form.branch_id,
        items:validItems};
      if(isNew){
        const r=await invoiceAPI.create(payload);
        if(form.update_stock&&r.data?.id){
          try{
            await invoiceAPI.deductStock(r.data.id);
            toast.success(`Invoice ${r.data.invoice_number} created & stock deducted`);
          }catch(e){
            // Invoice saved — stock deduction failed (show details from server)
            const errMsg=e.response?.data?.details?.join(', ')||e.response?.data?.error||'Stock deduction failed';
            toast.error(`Invoice created but stock deduction failed: ${errMsg}`,{duration:6000});
          }
        }else toast.success(`Invoice ${r.data.invoice_number} created`);
      }else{
        await invoiceAPI.update(invoice.id,payload);toast.success('Invoice updated');
      }
      setEditMode(false);onSaved();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save')}
    finally{setSaving(false)}
  };

  const changeStatus=async s=>{
    setStatusChanging(true);
    try{await invoiceAPI.update(invoice.id,{status:s});toast.success(`Status → ${s}`);setForm(f=>({...f,status:s}));onSaved();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
    finally{setStatusChanging(false)}
  };

  const doDelete=async()=>{
    try{await invoiceAPI.delete(invoice.id);toast.success(t('common.deleted'));onDeleted();}
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
  };

  const editable=!['paid','cancelled','refunded'].includes(form.status);
  const inputCls={fontSize:14,padding:'10px 12px',height:44,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:9,color:'var(--text)',boxSizing:'border-box'};
  const Lbl=({children})=><label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>{children}</label>;

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
      {/* Header */}
      <div style={{padding:isMobile?'14px 16px':'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10,flexShrink:0,background:'var(--surface)'}}>
        {isMobile&&<button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:'4px',display:'flex',flexShrink:0}}><ArrowLeft size={20}/></button>}
        <div style={{flex:1,minWidth:0}}>
          {isNew?<div style={{fontSize:16,fontWeight:800}}>{t('invoices.newInvoice')}</div>
            :<div><div style={{fontSize:15,fontWeight:800}}>{invoice.invoice_number}</div>
              <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{fmtDate(invoice.invoice_date)} · {invoice.customer?.name}</div>
              <div style={{display:'flex',gap:6,marginTop:4,flexWrap:'wrap',alignItems:'center'}}>
                {invoice.order&&<span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'rgba(99,102,241,.12)',color:'var(--primary)',border:'1px solid rgba(99,102,241,.25)'}}>
                  <FileText size={10}/> From Order: {invoice.order.order_number}
                </span>}
                {invoice.creator&&<span style={{fontSize:10,color:'var(--td)'}}>📝 <span style={{color:'var(--primary)',fontWeight:600}}>{invoice.creator.first_name} {invoice.creator.last_name}</span></span>}
                {invoice.updater&&<span style={{fontSize:10,color:'var(--td)'}}>✏️ <span style={{color:'var(--accent)',fontWeight:600}}>{invoice.updater.first_name} {invoice.updater.last_name}</span></span>}
              </div>
            </div>}
        </div>
        {!isNew&&<SPill s={form.status}/>}
        {!isNew&&editable&&!editMode&&can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>setEditMode(true)} style={{gap:4,fontSize:12}}><Edit2 size={13}/>{!isMobile&&' Edit'}</button>}
        {!isNew&&editable&&can.payment&&!editMode&&<button className="btn btn-success btn-sm" onClick={()=>onPay(invoice)} style={{gap:4,fontSize:12}}><DollarSign size={13}/>{!isMobile&&' '+t('invoices.recordPayment')}</button>}
        {!isNew&&!editMode&&<button className="btn btn-ghost btn-sm" onClick={()=>onPrint(invoice)} style={{padding:'5px 8px'}}><Printer size={13}/></button>}
        {!isNew&&editable&&can.delete&&!editMode&&(
          delConfirm
            ?<div style={{display:'flex',gap:5,alignItems:'center'}}>
                <button className="btn btn-danger btn-sm" style={{fontSize:11}} onClick={doDelete}>{t('common.delete')}</button>
                <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>setDelConfirm(false)}>No</button>
              </div>
            :<button className="btn btn-ghost btn-sm" style={{color:'var(--danger)',padding:'5px 7px'}} onClick={()=>setDelConfirm(true)}><Trash2 size={14}/></button>
        )}
        {!isMobile&&<button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',padding:4,display:'flex'}}><X size={18}/></button>}
      </div>

      {/* Body */}
      <div style={{flex:1,overflowY:'auto',padding:isMobile?'16px':'20px'}}>
        {/* Source Order reference banner */}
        {!isNew&&invoice.order&&(
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',marginBottom:14,borderRadius:10,background:'rgba(99,102,241,.08)',border:'1px solid rgba(99,102,241,.2)'}}>
            <FileText size={15} color="var(--primary)"/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--primary)',marginBottom:2}}>
                📋 Created from Sale Order / ផ្ដើមពីការបញ្ជាទិញ
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <span style={{fontSize:13,fontWeight:800,color:'var(--text)',fontFamily:'monospace',background:'var(--s2)',padding:'2px 8px',borderRadius:5,border:'1px solid var(--border)'}}>{invoice.order.order_number}</span>
                <button
                  onClick={()=>{navigator.clipboard?.writeText(invoice.order.order_number);}}
                  className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 7px',gap:3}}
                  title="Copy order number">
                  Copy
                </button>
                {invoice.creator&&<span style={{fontSize:10,color:'var(--td)'}}>
                  · Created by <span style={{fontWeight:600,color:'var(--primary)'}}>{invoice.creator.first_name} {invoice.creator.last_name}</span>
                </span>}
              </div>
            </div>
            {invoice.updater&&<div style={{textAlign:'right',flexShrink:0}}>
              <div style={{fontSize:10,color:'var(--td)'}}>Last edited by</div>
              <div style={{fontSize:11,fontWeight:600,color:'var(--accent)'}}>{invoice.updater.first_name} {invoice.updater.last_name}</div>
            </div>}
          </div>
        )}

        {/* Status stepper */}
        {!isNew&&(
          <div style={{marginBottom:16}}>
            <StatusStepper current={form.status} onChange={changeStatus} disabled={statusChanging} mobile={isMobile}/>
          </div>
        )}

        {/* Header fields */}
        <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:isMobile?12:'0 16px',marginBottom:16}}>
          <div>
            <Lbl>Customer *</Lbl>
            <select value={String(form.customer_id||'')} onChange={e=>sf('customer_id',e.target.value)} style={{...inputCls,border:!form.customer_id&&isNew?'1.5px solid var(--primary)':'1.5px solid var(--border)'}}>
              {custOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {isNew&&customers.length===0&&<div style={{fontSize:10,color:'var(--warning)',marginTop:3}}>⚠ No customers yet — add customers first</div>}
            {isNew&&customers.length>0&&!form.customer_id&&<div style={{fontSize:10,color:'var(--primary)',marginTop:3}}>👤 {customers.length} customer{customers.length!==1?'s':''} — select one to continue</div>}
          </div>
          <div>
            <Lbl>Branch *</Lbl>
            {branches.length<=1&&form.branch_id?(
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'10px 12px',background:'var(--pm)',border:'1.5px solid var(--primary)',borderRadius:9,fontSize:13,fontWeight:600,color:'var(--primary)'}}>
                <Lock size={12}/>{branches.find(b=>String(b.id)===String(form.branch_id))?.name||myBranch?.name||'Your Branch'}
              </div>
            ):(
              <select value={String(form.branch_id||'')} onChange={e=>sf('branch_id',e.target.value)} style={inputCls}>{brOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select>
            )}
            {invLoading&&<div style={{fontSize:10,color:'var(--primary)',marginTop:4,display:'flex',alignItems:'center',gap:4}}><RefreshCw size={9} style={{animation:'spin 1s linear infinite'}}/> Loading stock...</div>}
          </div>
          <div><Lbl>Invoice Date *</Lbl><input type="date" value={form.invoice_date||''} onChange={e=>sf('invoice_date',e.target.value)} style={inputCls}/></div>
          <div><Lbl>Due Date</Lbl><input type="date" value={form.due_date||''} onChange={e=>sf('due_date',e.target.value)} style={inputCls}/></div>
          {!isNew&&<div><Lbl>Status</Lbl><select value={form.status} onChange={e=>sf('status',e.target.value)} style={inputCls}>{statusOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></div>}
          <div><Lbl>Payment Terms</Lbl><input type="text" value={form.payment_terms||''} onChange={e=>sf('payment_terms',e.target.value)} placeholder="e.g. Net 30" style={inputCls}/></div>
        </div>

        {/* Line Items */}
        <div style={{marginBottom:8,display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 12px',background:'var(--s2)',borderRadius:9,borderLeft:'3px solid var(--primary)'}}>
          <div style={{display:'flex',alignItems:'center',gap:7}}>
            <Layers size={13} color="var(--primary)"/>
            <span style={{fontSize:12,fontWeight:700,letterSpacing:.4}}>{t('invoices.lineItems').toUpperCase()}</span>
            <span style={{fontSize:11,color:'var(--td)'}}>{validItems.length} item{validItems.length!==1?'s':''}</span>
            {form.branch_id&&!invLoading&&<span style={{fontSize:10,color:'var(--td)'}}>· stock from {branches.find(b=>String(b.id)===String(form.branch_id))?.name||myBranch?.name||''}</span>}
            {form.branch_id&&!invLoading&&branchProductIds&&branchProductIds.size>0&&branchProducts.length<products.length&&<span style={{fontSize:10,color:'var(--success)',fontWeight:600}}>{branchProducts.length} products stocked at branch</span>}
          </div>
          {editMode&&<button className="btn btn-ghost btn-sm" onClick={addItem} style={{fontSize:12,gap:4}}><Plus size={13}/> Add</button>}
        </div>

        {editMode?(
          isMobile?(
            <div style={{marginBottom:12}}>
              {form.items.length===0?<div style={{padding:24,textAlign:'center',color:'var(--td)',fontSize:13,background:'var(--s2)',borderRadius:10}}>No lines — tap Add</div>
                :form.items.map((item,i)=><MobileItemCard key={i} item={item} idx={i} products={branchProducts} invMap={invMap} branchId={parseInt(form.branch_id)||null} onChange={upItem} onRemove={rmItem}/>)}
            </div>
          ):(
            <div style={{background:'var(--surface)',borderRadius:9,border:'1px solid var(--border)',padding:'10px 10px 4px',marginBottom:12}}>
              {form.items.length===0?<div style={{padding:20,textAlign:'center',color:'var(--td)',fontSize:13}}>No lines — click Add</div>
                :form.items.map((item,i)=><DesktopItemRow key={i} item={item} idx={i} isFirst={i===0} products={branchProducts} invMap={invMap} branchId={parseInt(form.branch_id)||null} onChange={upItem} onRemove={rmItem} readOnly={false}/>)}
            </div>
          )
        ):(
          isMobile?(
            <div style={{marginBottom:12}}>
              {(form.items||[]).filter(i=>i.product_id).map((item,i)=>{
                const prod=products.find(p=>p.id===item.product_id);
                const lineTotal=+((item.quantity||0)*(item.unit_price||0)-(item.discount_amount||0)).toFixed(2);
                return(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'12px',background:'var(--s2)',borderRadius:10,marginBottom:6,border:'1px solid var(--border)'}}>
                    <div style={{width:36,height:36,borderRadius:8,background:'var(--pm)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Package size={15} color="var(--primary)"/></div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{prod?.name||'—'}</div>
                      <div style={{fontSize:11,color:'var(--td)'}}>{item.quantity} × {fmt(item.unit_price)}{parseFloat(item.discount_amount||0)>0?` − ${fmt(item.discount_amount)}`:''}</div>
                    </div>
                    <div style={{fontSize:14,fontWeight:800,color:'var(--primary)',flexShrink:0}}>{fmt(lineTotal)}</div>
                  </div>
                );
              })}
            </div>
          ):(
            <div style={{border:'1px solid var(--border)',borderRadius:10,overflow:'hidden',marginBottom:12}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'var(--s2)'}}>
                  {['#','Product','UOM','Qty','Unit Price','Discount','Amount'].map((h,i)=>(
                    <th key={h} style={{padding:'9px 12px',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:.4,color:'var(--td)',textAlign:i>=3?'right':'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(form.items||[]).filter(i=>i.product_id).map((item,i)=>(
                    <DesktopItemRow key={i} item={item} idx={i} isFirst={false} products={products} invMap={invMap} branchId={0} onChange={()=>{}} onRemove={()=>{}} readOnly={true}/>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Stock warnings */}
        {warnings.length>0&&editMode&&(
          <div style={{padding:'10px 14px',background:'var(--wm)',borderRadius:8,marginBottom:12,border:'1px solid rgba(245,158,11,.25)'}}>
            <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}><AlertTriangle size={13} color="var(--warning)"/><span style={{fontSize:12,fontWeight:700,color:'var(--warning)'}}>Stock Warning ({warnings.length})</span></div>
            {warnings.map(item=>{const p=products.find(x=>x.id===item.product_id);const inv=invMap[`${item.product_id}_${form.branch_id}`];return<div key={item.product_id} style={{fontSize:11,color:'var(--text)',marginBottom:2}}>· {p?.name}: needs <strong>{item.quantity}</strong>, have <strong>{inv?.quantity||0}</strong></div>})}
          </div>
        )}

        {/* Totals */}
        <div style={{background:'var(--s2)',borderRadius:10,padding:'14px 16px',marginBottom:14}}>
          {editMode&&(
            <div style={{display:'grid',gridTemplateColumns:isMobile?'1fr':'1fr 1fr',gap:isMobile?10:'0 12px',marginBottom:14}}>
              <div><Lbl>Tax Rate (%)</Lbl><input type="number" min={0} step={0.1} value={form.tax_rate||0} onChange={e=>sf('tax_rate',parseFloat(e.target.value)||0)} style={{...inputCls,background:'var(--surface)'}}/></div>
              <div><Lbl>Invoice Discount ($)</Lbl><input type="number" min={0} step={0.01} value={form.discount_amount||0} onChange={e=>sf('discount_amount',parseFloat(e.target.value)||0)} style={{...inputCls,background:'var(--surface)'}}/></div>
            </div>
          )}
          <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}><span style={{color:'var(--td)'}}>Subtotal</span><div style={{textAlign:'right'}}><span style={{fontWeight:600}}>{fmt(subtotal)}</span><div style={{fontSize:10,color:'var(--td)'}}>{fmtKHR(subtotal)}</div></div></div>
          {parseFloat(form.tax_rate||0)>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}><span style={{color:'var(--td)'}}>Tax ({form.tax_rate}%)</span><span>{fmt(taxAmt)}</span></div>}
          {parseFloat(form.discount_amount||0)>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginBottom:7}}><span style={{color:'var(--td)'}}>Discount</span><span style={{color:'var(--danger)'}}>-{fmt(form.discount_amount)}</span></div>}
          <div style={{display:'flex',justifyContent:'space-between',borderTop:'2px solid var(--border)',paddingTop:12,fontSize:18,fontWeight:900}}><span>TOTAL</span><div style={{textAlign:'right'}}><div style={{color:'var(--primary)'}}>{fmt(total)}</div><div style={{fontSize:11,fontWeight:400,color:'var(--td)',marginTop:2}}>{fmtKHR(total)}</div></div></div>
          {!isNew&&(
            <>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginTop:8,paddingTop:8,borderTop:'1px dashed var(--border)'}}><span style={{color:'var(--td)'}}>Paid</span><span style={{fontWeight:700,color:'var(--success)'}}>{fmt(invoice.paid_amount||0)}</span></div>
              {(()=>{const b=parseFloat(total)-parseFloat(invoice.paid_amount||0);return<div style={{display:'flex',justifyContent:'space-between',fontSize:14,fontWeight:800,padding:'8px 10px',borderRadius:8,background:b>0?'var(--dm)':'var(--sm)',marginTop:6}}><span style={{color:b>0?'var(--danger)':'var(--success)'}}>Balance</span><span style={{color:b>0?'var(--danger)':'var(--success)'}}>{fmt(b)}</span></div>})()}
            </>
          )}
        </div>

        {/* Stock deduct toggle (new only) */}
        {isNew&&editMode&&(
          <div onClick={()=>sf('update_stock',!form.update_stock)} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:form.update_stock?'var(--pm)':'var(--s2)',borderRadius:10,marginBottom:12,cursor:'pointer',border:`1px solid ${form.update_stock?'var(--primary)':'var(--border)'}`,transition:'all .15s'}}>
            <div style={{width:36,height:36,borderRadius:9,background:form.update_stock?'var(--primary)':'var(--border)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'background .15s'}}><Package size={17} color="#fff"/></div>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:form.update_stock?'var(--primary)':'var(--text)'}}>{form.update_stock?'✓ Deduct stock from '+((branches.find(b=>String(b.id)===String(form.branch_id))?.name||myBranch?.name||'branch')):' Deduct inventory on save'}</div><div style={{fontSize:11,color:'var(--td)',marginTop:2}}>Deducts BASE units using UoM conversion (e.g. 1 box × 24 = 24 bottles deducted). Prevents double deduction.</div></div>
            {form.update_stock?<ToggleRight size={22} color="var(--primary)"/>:<ToggleLeft size={22} color="var(--td)"/>}
          </div>
        )}

        {(editMode||form.notes)&&(
          <div style={{marginBottom:16}}>
            <Lbl>Notes / Payment Terms</Lbl>
            <textarea value={form.notes||''} onChange={e=>sf('notes',e.target.value)} placeholder="Internal notes, terms, or instructions..."
              style={{width:'100%',fontSize:13,padding:'10px 12px',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:9,color:'var(--text)',resize:'vertical',height:72,boxSizing:'border-box',fontFamily:'inherit'}}/>
          </div>
        )}

        {/* Payment history (view mode) */}
        {!isNew&&!editMode&&(invoice.payments||[]).length>0&&(
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.5,marginBottom:8}}>Payment History</div>
            {(invoice.payments||[]).map((p,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--s2)',borderRadius:8,marginBottom:5,border:'1px solid var(--border)'}}>
                <DollarSign size={14} color="var(--success)"/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--success)'}}>{fmt(p.amount)}</div>
                  <div style={{fontSize:11,color:'var(--td)'}}>{fmtDate(p.payment_date)} · {p.method?.name||'—'}{p.reference?` · ${p.reference}`:''}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {editMode&&(
          <div style={{display:'flex',gap:10,paddingTop:16,borderTop:'1px solid var(--border)'}}>
            {!isNew&&<button className="btn btn-ghost" onClick={()=>setEditMode(false)} style={{flex:1,height:46,fontSize:14}}>{t('common.cancel')}</button>}
            <button className="btn btn-primary" onClick={save} disabled={saving} style={{flex:2,height:46,fontSize:14,gap:7}}>
              {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> {t('common.saving')}</>:<><Save size={14}/> {isNew?t('invoices.newInvoice'):t('orders.saveChanges')}</>}
            </button>
          </div>
        )}
        <div style={{height:20}}/>
      </div>
    </div>
  );
}

/* ── Cambodia Classic Invoice Print (shared component) ──────── */
function KhInvoicePrint({doc, type='invoice', settings, khr=4100}){
  // doc = invoice or order object
  const fmtKHR=n=>`${Math.round((parseFloat(n)||0)*khr).toLocaleString()}`;
  const subtotal=parseFloat(doc.subtotal||0);
  const tax=parseFloat(doc.tax_amount||0);
  const disc=parseFloat(doc.discount_amount||0);
  const total=parseFloat(doc.total_amount||0);
  const paid=parseFloat(doc.paid_amount||0);
  const isInvoice=type==='invoice';

  const cellStyle={border:'1px solid #000',padding:'4px 6px',fontSize:11,verticalAlign:'middle'};
  const thStyle={...cellStyle,background:'#fff',fontWeight:700,textAlign:'center',fontSize:10};
  const PAGE={fontFamily:"'Noto Serif Khmer','Khmer OS','Battambang',Arial,sans-serif",background:'#fff',color:'#000',padding:'18px 24px',maxWidth:740,margin:'0 auto',fontSize:12};

  // Pad items to at least 8 rows for print appearance
  const items=doc.items||[];
  const padded=[...items,...Array(Math.max(0,8-items.length)).fill(null)];

  const biz=settings||{};
  const docNum=doc.invoice_number||doc.order_number||'—';
  const docDate=doc.invoice_date||doc.order_date;
  const docDue=doc.due_date;
  const docTitle=isInvoice?'វិក្កយបត្រ / INVOICE':'ការបញ្ជាទិញ / SALE ORDER';

  return(
    <div id="inv-print-area" style={PAGE}>

      {/* ── TOP HEADER ── */}
      <div style={{textAlign:'center',marginBottom:8}}>
        {biz.logo_url&&<img src={biz.logo_url} alt="" style={{height:48,marginBottom:4,objectFit:'contain'}}/>}
        {biz.company_name&&<div style={{fontSize:13,fontWeight:700}}>{biz.company_name}</div>}
        {biz.company_address&&<div style={{fontSize:10,color:'#333'}}>Address : {biz.company_address}</div>}
        {biz.company_phone&&<div style={{fontSize:10,color:'#333'}}>ទូរស័ព្ទ(Telephone No) : {biz.company_phone}</div>}
        {biz.company_email&&<div style={{fontSize:10,color:'#333'}}>អ៊ីម៉ែល(E-mail) : {biz.company_email}</div>}
        <div style={{fontSize:16,fontWeight:900,color:'#1a56db',marginTop:6,letterSpacing:1}}>{docTitle}</div>
        <div style={{borderTop:'2px solid #000',marginTop:4}}/>
      </div>

      {/* ── META INFO (2-col grid like reference image) ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:0,marginBottom:8,fontSize:11}}>
        {/* Left column */}
        <div style={{paddingRight:8}}>
          {[
            ['លេខកូដ (Code)', doc.customer?.code||'—'],
            ['ឈ្មោះក្រុមហ៊ុន (Company)', doc.customer?.name||'—'],
            ['ទូរស័ព្ទ (Telephone No)', doc.customer?.phone||'—'],
            ['ទំនាក់ទំនង (Contact)', doc.customer?.contact||'—'],
            ['អាសយដ្ឋាន (Address)', doc.customer?.address||doc.branch?.address||'—'],
          ].map(([lbl,val])=>(
            <div key={lbl} style={{display:'flex',gap:4,marginBottom:3,lineHeight:1.4}}>
              <span style={{color:'#555',minWidth:160,flexShrink:0,fontSize:10}}>{lbl}</span>
              <span style={{fontWeight:600,fontSize:11}}>: {val}</span>
            </div>
          ))}
        </div>
        {/* Right column */}
        <div style={{paddingLeft:8}}>
          {[
            ['លេខវិក្កយបត្រ (Invoice No)', docNum],
            ['កាលបរិច្ឆេទ (Date)', fmtDate(docDate)],
            ...(docDue?[['ថ្ងៃផុតកំណត់ (Due Date)', fmtDate(docDue)]]:[]),
            ...(isInvoice&&doc.order?.order_number?[['លេខបញ្ជាទិញ (Order Ref)', doc.order.order_number]]:[]),
            ['រូបិយប័ណ្ណ (Currency)', 'ដុល្លារ (USD)'],
            ['សាខា (Branch)', doc.branch?.name||'—'],
          ].map(([lbl,val])=>(
            <div key={lbl} style={{display:'flex',gap:4,marginBottom:3,lineHeight:1.4}}>
              <span style={{color:'#555',minWidth:160,flexShrink:0,fontSize:10}}>{lbl}</span>
              <span style={{fontWeight:600,fontSize:11}}>: {val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── ITEMS TABLE ── */}
      <table style={{width:'100%',borderCollapse:'collapse',marginBottom:0,border:'1px solid #000'}}>
        <thead>
          <tr>
            <th style={{...thStyle,width:28}}>ល.រ<br/><span style={{fontWeight:400}}>No</span></th>
            <th style={{...thStyle,width:90}}>លេខកូដ<br/><span style={{fontWeight:400}}>Code</span></th>
            <th style={{...thStyle}}>បរិយាយទំនិញ<br/><span style={{fontWeight:400}}>Description</span></th>
            <th style={{...thStyle,width:45}}>បញ្ចុះតម្លៃ<br/><span style={{fontWeight:400}}>Dis Perc</span></th>
            <th style={{...thStyle,width:55}}>បរិមាណ<br/><span style={{fontWeight:400}}>Quantity</span></th>
            <th style={{...thStyle,width:60}}>ឯកតា<br/><span style={{fontWeight:400}}>UoM</span></th>
            <th style={{...thStyle,width:65}}>ថ្លៃដើម<br/><span style={{fontWeight:400}}>Unit Price</span></th>
            <th style={{...thStyle,width:65}}>វិភាជន៍<br/><span style={{fontWeight:400}}>Amount</span></th>
          </tr>
        </thead>
        <tbody>
          {padded.map((item,i)=>(
            <tr key={i} style={{height:22}}>
              <td style={{...cellStyle,textAlign:'center',fontSize:10}}>{item?i+1:''}</td>
              <td style={{...cellStyle,fontSize:10}}>{item?.product?.sku||''}</td>
              <td style={{...cellStyle}}>
                {item&&<>
                  <div style={{fontWeight:600,fontSize:11}}>{item.product?.name_kh||item.product?.name||'—'}</div>
                  {item.product?.name_kh&&<div style={{fontSize:9,color:'#555'}}>{item.product?.name}</div>}
                </>}
              </td>
              <td style={{...cellStyle,textAlign:'center',fontSize:10}}>
                {item&&parseFloat(item.discount_amount||0)>0?`${((parseFloat(item.discount_amount)/parseFloat(item.unit_price||1))*100).toFixed(0)} %`:''}
              </td>
              <td style={{...cellStyle,textAlign:'center'}}>{item?.quantity||''}</td>
              <td style={{...cellStyle,textAlign:'center',fontSize:10}}>
                {item&&(item.uom?.abbreviation||item.uom?.name||item.product?.uom?.abbreviation||'—')}
              </td>
              <td style={{...cellStyle,textAlign:'right'}}>{item?`$${parseFloat(item.unit_price||0).toFixed(3)}`:''}</td>
              <td style={{...cellStyle,textAlign:'right',fontWeight:item?600:400}}>
                {item?`$${parseFloat(item.line_total||item.quantity*item.unit_price||0).toFixed(2)}`:''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ── BOTTOM: bank detail left, totals right ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',border:'1px solid #000',borderTop:'none'}}>
        {/* Bank Detail */}
        <div style={{borderRight:'1px solid #000',padding:'6px 8px',fontSize:10}}>
          <div style={{fontWeight:900,marginBottom:4,fontSize:11,textDecoration:'underline'}}>Bank Detail:</div>
          {biz.bank_name&&<div style={{marginBottom:2}}><span style={{fontWeight:700}}>Bank Name</span> : {biz.bank_name}</div>}
          {biz.bank_address&&<div style={{marginBottom:2}}><span style={{fontWeight:700}}>Bank Address</span> : {biz.bank_address}</div>}
          {biz.account_name&&<div style={{marginBottom:2}}><span style={{fontWeight:700}}>Account Name</span> : {biz.account_name}</div>}
          {biz.account_number&&<div style={{marginBottom:2}}><span style={{fontWeight:700}}>Acct Number</span> : {biz.account_number}</div>}
          {!biz.bank_name&&<div style={{color:'#999',fontStyle:'italic'}}>— Add bank details in Settings —</div>}
          {/* Payment info */}
          {isInvoice&&(doc.payments||[]).length>0&&(
            <div style={{marginTop:6}}>
              <div style={{fontWeight:700,marginBottom:3}}>Payment:</div>
              {(doc.payments||[]).map((p,i)=>(
                <div key={i}>{p.method?.name||'Cash'} : ${parseFloat(p.amount).toFixed(2)}</div>
              ))}
            </div>
          )}
        </div>
        {/* Totals */}
        <div style={{fontSize:11}}>
          {[
            ['សរុប (Total)', `$${subtotal.toFixed(2)}`],
            ...(disc>0?[['បញ្ចុះតម្លៃ (Discount)', `($${disc.toFixed(2)})`]]:[]),
            ...(tax>0?[['អាករ (Tax)', `$${tax.toFixed(2)}`]]:[]),
          ].map(([lbl,val])=>(
            <div key={lbl} style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',borderBottom:'1px solid #ccc'}}>
              <span style={{color:'#555'}}>{lbl}</span>
              <span style={{fontWeight:600}}>{val}</span>
            </div>
          ))}
          <div style={{display:'flex',justifyContent:'space-between',padding:'6px 10px',fontWeight:900,fontSize:13,background:'#f0f4ff'}}>
            <span>សរុបរួម (Grand Total)</span>
            <span>${total.toFixed(2)}</span>
          </div>
          {isInvoice&&paid>0&&<div style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',borderTop:'1px solid #ccc',color:'#15803d'}}>
            <span>ទូទាត់រួច (Paid)</span>
            <span style={{fontWeight:700}}>${paid.toFixed(2)}</span>
          </div>}
          {isInvoice&&(total-paid)>0.01&&<div style={{display:'flex',justifyContent:'space-between',padding:'4px 10px',borderTop:'1px solid #ccc',color:'#dc2626'}}>
            <span>នៅខ្វះ (Balance)</span>
            <span style={{fontWeight:700}}>${(total-paid).toFixed(2)}</span>
          </div>}
          {/* KHR equiv */}
          <div style={{textAlign:'right',padding:'3px 10px',fontSize:9,color:'#888'}}>
            ≈ ៛{fmtKHR(total)} (KHR)
          </div>
        </div>
      </div>

      {/* ── NOTES ── */}
      {doc.notes&&<div style={{border:'1px solid #ccc',borderTop:'none',padding:'5px 8px',fontSize:10,color:'#555'}}>
        📝 {doc.notes}
      </div>}

      {/* ── SIGNATURES ── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:40,marginTop:24,marginBottom:8}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:12,fontWeight:700}}>ហត្ថលេខា និង ឈ្មោះអតិថិជន</div>
          <div style={{fontSize:10,color:'#555',marginBottom:20}}>Customer's Signature and Name</div>
          <div style={{borderTop:'1px solid #000',paddingTop:4}}/>
        </div>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:12,fontWeight:700}}>ហត្ថលេខា និង ឈ្មោះអ្នកលក់</div>
          <div style={{fontSize:10,color:'#555',marginBottom:20}}>Seller's Signature &amp; Name</div>
          <div style={{borderTop:'1px solid #000',paddingTop:4}}/>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{textAlign:'center',marginTop:10,borderTop:'1px solid #ccc',paddingTop:6}}>
        <div style={{fontSize:11}}>🙏 សូមអរគុណ! / Thank you! Please come again</div>
        <div style={{fontSize:9,color:'#999',marginTop:2}}>Page 1 of 1</div>
      </div>

    </div>
  );
}

/* ── Cambodia Bilingual Print Invoice ──────────────────────────── */
function PrintModal({inv,settings,onClose}){
  const[khr,setKhr]=useState(4100);
  useEffect(()=>{currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setKhr(parseFloat(rt.rate)||4100);}).catch(()=>{});},[]); 
  return(
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.75)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:800,maxHeight:'94vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:14}}>🖨 វិក្កយបត្រ / Invoice — {inv.invoice_number}</span>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary btn-sm" onClick={()=>openPrintWindow('inv-print-area')} style={{gap:5}}>
              <Printer size={13}/> Print / PDF
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={14}/></button>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',background:'#e5e7eb'}}>
          <div style={{padding:16}}>
            <KhInvoicePrint doc={inv} type="invoice" settings={settings} khr={khr}/>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ── Payment Modal ───────────────────────────────────────────── */
function PayModal({inv,methods,onSave,onClose}){
  const{t}=useLang();
  const bal=Math.max(0,parseFloat(inv.total_amount||0)-parseFloat(inv.paid_amount||0));
  const[currency,setCurrency]=useState('USD');
  const[form,setForm]=useState({amount:bal.toFixed(2),payment_method_id:methods[0]?.id||'',reference:'',notes:''});
  const[saving,setSaving]=useState(false);
  const[panelKhrRate,setPanelKhrRate]=useState(4100);
  useEffect(()=>{currencyAPI.getRates().then(r=>{const rt=(r.data?.data||r.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');if(rt)setPanelKhrRate(parseFloat(rt.rate)||4100);}).catch(()=>{});
    // Init KHR amount on first load
  },[]);
  const fmtKHR=n=>`≈ ៛${Math.round((parseFloat(n)||0)*panelKhrRate).toLocaleString()}`;

  // USD equiv of what was entered
  const amountUSD=currency==='KHR'
    ?+((parseFloat(form.amount)||0)/panelKhrRate).toFixed(2)
    :parseFloat(form.amount)||0;
  const amountKHR=currency==='KHR'
    ?parseFloat(form.amount)||0
    :Math.round((parseFloat(form.amount)||0)*panelKhrRate);

  const switchCurrency=v=>{
    // Convert current amount to new currency on switch
    const cur=parseFloat(form.amount)||0;
    if(v==='KHR'&&currency==='USD') setForm(f=>({...f,amount:String(Math.round(cur*panelKhrRate))}));
    if(v==='USD'&&currency==='KHR') setForm(f=>({...f,amount:((cur/panelKhrRate).toFixed(2))}));
    setCurrency(v);
  };

  const methodOpts=[{value:'',label:'— Select method —'},...methods.map(m=>({value:String(m.id),label:m.name}))];
  const Lbl=({children,sub})=><label style={{display:'flex',alignItems:'baseline',gap:6,fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>{children}{sub&&<span style={{fontSize:10,fontWeight:400,textTransform:'none',letterSpacing:0,color:'var(--primary)'}}>{sub}</span>}</label>;
  const inputCls={fontSize:14,padding:'10px 12px',height:44,width:'100%',background:'var(--s2)',border:'1.5px solid var(--border)',borderRadius:9,color:'var(--text)',boxSizing:'border-box'};

  const save=async()=>{
    if(!form.amount||amountUSD<=0)return toast.error('Enter a valid amount');
    if(amountUSD>bal+0.01)return toast.error(`Exceeds balance due: ${fmt(bal)}`);
    setSaving(true);
    try{
      await onSave({
        amount:amountUSD,
        original_amount:parseFloat(form.amount)||0,
        original_currency:currency,
        exchange_rate:panelKhrRate,
        payment_method_id:parseInt(form.payment_method_id)||null,
        reference:form.reference,
        notes:form.notes,
      });
      onClose();
    }
    catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'))}
    finally{setSaving(false)}
  };

  const quickAmountsUSD=[5,10,20,50,100];
  const quickAmountsKHR=[5000,10000,20000,50000,100000];

  return(
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'var(--surface)',borderRadius:14,width:'100%',maxWidth:460,overflow:'hidden'}}>
        {/* Header */}
        <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontSize:15,fontWeight:700}}>Record Payment — {inv.invoice_number}</span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',display:'flex'}}><X size={18}/></button>
        </div>
        <div style={{padding:20}}>
          {/* Total / Balance cards */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
            <div style={{background:'var(--s2)',borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:10,color:'var(--td)',fontWeight:600,letterSpacing:.4,marginBottom:4}}>TOTAL</div>
              <div style={{fontSize:20,fontWeight:800}}>{fmt(inv.total_amount)}</div>
              <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{fmtKHR(parseFloat(inv.total_amount||0))}</div>
            </div>
            <div style={{background:'var(--dm)',borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:10,color:'var(--danger)',fontWeight:600,letterSpacing:.4,marginBottom:4}}>BALANCE DUE</div>
              <div style={{fontSize:20,fontWeight:800,color:'var(--danger)'}}>{fmt(bal)}</div>
              <div style={{fontSize:11,color:'var(--td)',marginTop:2}}>{fmtKHR(bal)}</div>
            </div>
          </div>

          {/* Currency toggle */}
          <div style={{marginBottom:14}}>
            <Lbl>Payment Currency</Lbl>
            <div style={{display:'flex',background:'var(--s2)',borderRadius:9,padding:3,gap:3,border:'1px solid var(--border)'}}>
              {[['USD','$ USD'],['KHR','៛ KHR']].map(([val,label])=>(
                <button key={val} onClick={()=>switchCurrency(val)}
                  style={{flex:1,padding:'8px 0',borderRadius:7,border:'none',cursor:'pointer',fontSize:13,fontWeight:700,
                    background:currency===val?'var(--primary)':'transparent',
                    color:currency===val?'#fff':'var(--td)',transition:'all .15s'}}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{display:'grid',gap:12}}>
            {/* Amount field */}
            <div>
              <Lbl sub={currency==='KHR'?`≈ ${fmt(amountUSD)} USD`:fmtKHR(amountUSD)}>
                Amount * <span style={{fontSize:11,color:'var(--td)',fontWeight:400,textTransform:'none'}}>({currency})</span>
              </Lbl>
              <div style={{position:'relative'}}>
                <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,fontWeight:700,color:'var(--td)',pointerEvents:'none'}}>
                  {currency==='KHR'?'៛':'$'}
                </span>
                <input type="number" min={0} step={currency==='KHR'?100:0.01}
                  value={form.amount}
                  onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
                  style={{...inputCls,paddingLeft:28,fontSize:18,fontWeight:800,
                    border:`2px solid ${amountUSD>bal+0.01?'var(--danger)':'var(--primary)'}`,
                    color:amountUSD>bal+0.01?'var(--danger)':'var(--text)'}}/>
              </div>
              {/* Conversion display */}
              {parseFloat(form.amount)>0&&<div style={{marginTop:5,display:'flex',justifyContent:'space-between',fontSize:11}}>
                <span style={{color:'var(--td)'}}>
                  {currency==='KHR'
                    ?<>= <strong style={{color:'var(--success)'}}>{fmt(amountUSD)}</strong> USD</>
                    :fmtKHR(amountUSD)}
                </span>
                {amountUSD>bal+0.01&&<span style={{color:'var(--danger)',fontWeight:700}}>⚠ Exceeds balance</span>}
                {amountUSD<=bal&&Math.abs(amountUSD-bal)>0.01&&<span style={{color:'var(--td)'}}>Remaining: {fmt(bal-amountUSD)}</span>}
                {Math.abs(amountUSD-bal)<=0.01&&<span style={{color:'var(--success)',fontWeight:700}}>✓ Full payment</span>}
              </div>}
              {/* Quick amount buttons */}
              <div style={{display:'flex',gap:5,marginTop:8,flexWrap:'wrap'}}>
                {(currency==='KHR'?quickAmountsKHR:quickAmountsUSD).map(a=>(
                  <button key={a} onClick={()=>setForm(f=>({...f,amount:String(a)}))}
                    className="btn btn-ghost btn-sm"
                    style={{fontSize:11,padding:'4px 9px',fontWeight:700}}>
                    {currency==='KHR'?`៛${a>=1000?a/1000+'K':a}`:`$${a}`}
                  </button>
                ))}
                <button onClick={()=>setForm(f=>({...f,amount:currency==='KHR'?String(Math.round(bal*panelKhrRate)):bal.toFixed(2)}))}
                  className="btn btn-ghost btn-sm"
                  style={{fontSize:11,padding:'4px 9px',fontWeight:700,color:'var(--primary)'}}>
                  Exact
                </button>
              </div>
            </div>

            {/* Method */}
            <div>
              <Lbl>Method</Lbl>
              <select value={String(form.payment_method_id||'')} onChange={e=>setForm(f=>({...f,payment_method_id:e.target.value}))} style={inputCls}>
                {methodOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {/* Reference */}
            <div>
              <Lbl>Reference</Lbl>
              <input type="text" value={form.reference||''} onChange={e=>setForm(f=>({...f,reference:e.target.value}))} placeholder="e.g. TXN-12345" style={inputCls}/>
            </div>
            {/* Notes */}
            <div>
              <Lbl>Notes</Lbl>
              <textarea value={form.notes||''} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} style={{...inputCls,height:60,resize:'vertical',fontFamily:'inherit'}}/>
            </div>
          </div>

          <div style={{display:'flex',gap:10,marginTop:16}}>
            <button className="btn btn-ghost" onClick={onClose} style={{flex:1,height:46,fontSize:14}}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={save} disabled={saving||amountUSD<=0||amountUSD>bal+0.01}
              style={{flex:2,height:46,fontSize:14,gap:7,opacity:amountUSD>bal+0.01?.6:1}}>
              {saving
                ?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Saving…</>
                :<><DollarSign size={14}/> Record {currency==='KHR'?`៛${Math.round(amountKHR).toLocaleString()}`:`${fmt(amountUSD)}`}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
export default function InvoicesPage(){
  const{t}=useLang();
  const{hasPermission,isOwner,myBranchId,myBranch}=useAuth();
  const can={view:hasPermission(PERMS.VIEW),create:hasPermission(PERMS.CREATE),edit:hasPermission(PERMS.EDIT),delete:hasPermission(PERMS.DELETE),payment:hasPermission(PERMS.PAYMENT)};
  const w=useWindowWidth();const isMobile=w<768;const isTablet=w>=768&&w<1100;

  const[data,setData]=useState([]);
  const[customers,setCustomers]=useState([]);const[products,setProducts]=useState([]);
  const[branches,setBranches]=useState([]);const[methods,setMethods]=useState([]);const[settings,setSettings]=useState({});
  const[ld,setLd]=useState(true);const[search,setSearch]=useState('');const[statusF,setStatusF]=useState('All');
  const[isNew,setIsNew]=useState(false);const[panelInvoice,setPanelInvoice]=useState(null);
  const[printInv,setPrintInv]=useState(null);const[payInv,setPayInv]=useState(null);

  // Track branch-level inventory for product filtering
  const[branchInventory,setBranchInventory]=useState([]);
  const[metaLoaded,setMetaLoaded]=useState(false);
  const[khrRate,setKhrRate]=useState(4100);
  const fmtKHR=n=>`≈ ៛${Math.round(n*khrRate).toLocaleString()}`;

  useEffect(()=>{
    // print handled by openPrintWindow popup
    setMetaLoaded(false);
    const branchParam=(!isOwner&&myBranchId)?myBranchId:null;
    // Customer filtering is handled server-side (returns branch + unassigned customers)
    const custParams={limit:500};
    Promise.allSettled([
      customerAPI.getAll(custParams),
      productAPI.getAll({limit:500,is_active:true}),
      branchAPI.getAll({limit:100}),
      paymentAPI.getMethods(),
      settingsAPI.getAll(),
      branchParam?inventoryAPI.getAll({branch_id:branchParam,limit:1000}):Promise.resolve({data:{data:[]}}),
      currencyAPI.getRates(),
    ]).then(([cr,pr,br,mr,sr,ir,rr])=>{
      const khrR=rr.status==='fulfilled'&&(rr.value?.data?.data||rr.value?.data||[]).find(x=>x.from_currency==='USD'&&x.to_currency==='KHR');
      if(khrR)setKhrRate(parseFloat(khrR.rate)||4100);
      if(cr.status==='fulfilled') setCustomers(cr.value.data.data||[]);
      if(pr.status==='fulfilled') setProducts(pr.value.data.data||[]);
      if(br.status==='fulfilled') setBranches(br.value.data.data||[]);
      if(mr.status==='fulfilled') setMethods(mr.value.data.data||mr.value.data||[]);
      if(sr.status==='fulfilled'){const o={};(sr.value.data.data||sr.value.data||[]).forEach(x=>{o[x.key]=x.value});setSettings(o);}
      if(ir.status==='fulfilled') setBranchInventory(ir.value.data.data||[]);
      setMetaLoaded(true);
    });
  },[isOwner,myBranchId]);

  const load=useCallback(async()=>{
    try{const p={search,limit:200};if(statusF!=='All')p.status=statusF;if(!isOwner&&myBranchId)p.branch_id=myBranchId;const r=await invoiceAPI.getAll(p);setData(r.data.data||[])}
    catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)};
  },[search,statusF,isOwner,myBranchId]);
  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t)},[load]);

  const openInv=async inv=>{
    try{const{data:d}=await invoiceAPI.getById(inv.id);setPanelInvoice(d);setIsNew(false);}
    catch{toast.error(t('common.failedToSave'))}
  };
  const openNew=()=>{setPanelInvoice({});setIsNew(true);};
  const closePanel=()=>{setPanelInvoice(null);setIsNew(false);};
  const onSaved=()=>{load();if(!isNew&&panelInvoice)invoiceAPI.getById(panelInvoice.id).then(r=>setPanelInvoice(r.data)).catch(()=>{})};
  const onDeleted=()=>{closePanel();load();};
  const openPrint=async inv=>{try{const{data:d}=await invoiceAPI.getById(inv.id);setPrintInv(d)}catch{toast.error(t('common.failedToSave'))}};
  const openPay=inv=>setPayInv(inv);
  const recordPay=async form=>{await invoiceAPI.recordPayment(payInv.id,form);toast.success(t('invoices.paymentRecorded'));setPayInv(null);load();if(panelInvoice?.id===payInv.id)openInv(panelInvoice)};

  const showPanel=panelInvoice!==null||isNew;
  const showList=!isMobile||!showPanel;
  const listWidth=showPanel?(isTablet?'340px':'420px'):'100%';

  // Stats
  const totalInv=data.reduce((a,r)=>a+parseFloat(r.total_amount||0),0);
  const totalPaid=data.reduce((a,r)=>a+parseFloat(r.paid_amount||0),0);

  return(
    <div style={{display:'flex',height:'calc(100vh - 60px)',overflow:'hidden',position:'relative'}}>

      {/* LIST PANE */}
      {showList&&(
        <div style={{display:'flex',flexDirection:'column',width:listWidth,minWidth:showPanel?'300px':undefined,flexShrink:0,borderRight:showPanel?'1px solid var(--border)':'none',overflow:'hidden',background:'var(--surface)'}}>

          {/* Toolbar */}
          <div style={{padding:isMobile?'14px 14px 0':'16px 16px 0',flexShrink:0}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <div>
                <div style={{fontSize:isMobile?16:18,fontWeight:800}}>{t('invoices.title')}</div>
                <div style={{fontSize:12,color:'var(--td)',marginTop:1}}>{data.length} invoices · {fmt(totalInv)} total</div>
              </div>
              {!isOwner&&myBranch&&<div style={{display:'flex',alignItems:'center',gap:5,fontSize:11,padding:'5px 8px',background:'var(--pm)',borderRadius:7,color:'var(--primary)',fontWeight:600,marginBottom:6}}><span>🔒</span>{myBranch.name}</div>}
            {can.create&&<button className="btn btn-primary" onClick={openNew} style={{gap:5,height:38,fontSize:13,padding:'0 14px'}}><Plus size={14}/>{!isMobile&&' '+t('invoices.newInvoice')}</button>}
            </div>
            {!can.view&&<NoPerm action="view invoices"/>}
            <SearchBar value={search} onChange={setSearch} placeholder="Search invoice #, order #, customer..."/>
            <div style={{marginTop:8,overflowX:'auto',paddingBottom:4,marginLeft:-2}}>
              <FilterButtons options={FILTER_TABS} active={statusF} onChange={setStatusF}/>
            </div>
          </div>

          {/* Stats strip (hidden when panel is open on small screens) */}
          {(!showPanel||!isTablet)&&!isMobile&&(
            <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',flexShrink:0}}>
              {[{label:'Invoiced',val:fmt(totalInv),sub:fmtKHR(totalInv)},{label:'Collected',val:fmt(totalPaid),color:'var(--success)',sub:fmtKHR(totalPaid)},{label:'Outstanding',val:fmt(totalInv-totalPaid),color:totalInv-totalPaid>0?'var(--warning)':'var(--success)',sub:fmtKHR(totalInv-totalPaid)}].map(({label,val,color,sub})=>(
                <div key={label} style={{flex:1,padding:'8px 12px',borderRight:'1px solid var(--border)'}}>
                  <div style={{fontSize:9,color:'var(--td)',fontWeight:700,textTransform:'uppercase',letterSpacing:.4,marginBottom:2}}>{label}</div>
                  <div style={{fontSize:13,fontWeight:800,color:color||'var(--text)'}}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Invoice Cards */}
          <div style={{flex:1,overflowY:'auto',padding:isMobile?'8px 10px':'8px'}}>
            {ld?<div style={{padding:40,textAlign:'center',color:'var(--td)',fontSize:13}}>Loading…</div>
              :data.length===0?<div style={{padding:40,textAlign:'center',color:'var(--td)',fontSize:13}}><FileText size={32} style={{opacity:.2,display:'block',margin:'0 auto 12px'}}/>{t('invoices.noInvoices')}</div>
              :data.map(inv=>{
                const isActive=!isNew&&panelInvoice?.id===inv.id;
                const bal=parseFloat(inv.total_amount||0)-parseFloat(inv.paid_amount||0);
                return(
                  <div key={inv.id} onClick={()=>openInv(inv)}
                    style={{padding:isMobile?'13px 14px':'12px 14px',borderRadius:10,marginBottom:4,cursor:'pointer',border:`1.5px solid ${isActive?'var(--primary)':'var(--border)'}`,background:isActive?'var(--pm)':'var(--surface)',transition:'all .12s'}}
                    onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background='var(--s2)'}}
                    onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='var(--surface)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <span style={{fontSize:12,fontWeight:800,color:isActive?'var(--primary)':'var(--accent)',fontFamily:'monospace',flex:1}}>{inv.invoice_number}</span>
                      <SPill s={inv.status} size={10}/>
                      {isActive&&!isMobile&&<ChevronRight size={14} color="var(--primary)"/>}
                    </div>
                    <div style={{fontSize:13,color:'var(--td)',marginBottom:4,fontWeight:500}}>{inv.customer?.name||'—'}</div>
                    {inv.order&&<div style={{marginBottom:4}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:5,background:'rgba(99,102,241,.1)',color:'var(--primary)',border:'1px solid rgba(99,102,241,.2)'}}>
                        <FileText size={9}/> Ref Order: {inv.order.order_number}
                      </span>
                    </div>}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <span style={{fontSize:11,color:'var(--td)'}}>{fmtDate(inv.invoice_date)}{inv.branch?.name?` · ${inv.branch.name}`:''}</span>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:14,fontWeight:800,color:'var(--text)'}}>{fmt(inv.total_amount)}</div><div style={{fontSize:10,color:'var(--td)',marginTop:1}}>{fmtKHR(parseFloat(inv.total_amount||0))}</div>
                        {bal>0&&<div style={{fontSize:10,color:'var(--danger)',fontWeight:700}}>Bal: {fmt(bal)}</div>}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:10,marginTop:4,flexWrap:'wrap'}}>
                      {inv.creator&&<span style={{fontSize:10,color:'var(--td)',display:'flex',alignItems:'center',gap:3}}>
                        <span style={{color:'var(--tm)'}}>By:</span> <span style={{fontWeight:600,color:'var(--primary)'}}>{inv.creator.first_name} {inv.creator.last_name}</span>
                      </span>}
                      {inv.updater&&<span style={{fontSize:10,color:'var(--td)',display:'flex',alignItems:'center',gap:3}}>
                        <span style={{color:'var(--tm)'}}>Edited:</span> <span style={{fontWeight:600,color:'var(--accent)'}}>{inv.updater.first_name} {inv.updater.last_name}</span>
                      </span>}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* DETAIL PANE */}
      {showPanel&&(
        <div style={{flex:isMobile?undefined:1,width:isMobile?'100%':undefined,...(isMobile?{position:'absolute',inset:0,zIndex:100,background:'var(--surface)'}:{}),overflow:'hidden',display:'flex',flexDirection:'column'}}>
          {!metaLoaded?(
            <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--td)',fontSize:13,gap:8}}>
              <RefreshCw size={16} style={{animation:'spin 1s linear infinite'}}/> Loading…
            </div>
          ):(
          <InvoicePanel
            key={isNew?'new':panelInvoice?.id}
            invoice={panelInvoice||{}}
            isNew={isNew}
            customers={customers}
            products={products}
            branches={isOwner?branches:branches.filter(b=>Number(b.id)===Number(myBranchId))}
            branchInventory={branchInventory}
            methods={methods}can={can}
            onSaved={onSaved}onDeleted={onDeleted}
            onPay={inv=>{openPay(inv)}}
            onPrint={openPrint}
            onClose={closePanel}isMobile={isMobile}
          />
          )}
        </div>
      )}

      {/* Print overlay */}
      {printInv&&<PrintModal inv={printInv} settings={settings} onClose={()=>setPrintInv(null)}/>}

      {/* Pay overlay */}
      {payInv&&<PayModal inv={payInv} methods={methods} onSave={recordPay} onClose={()=>setPayInv(null)}/>}
    </div>
  );
}
